import axios from 'axios'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { authService } from '@/services/auth_service'
import { useMe } from '@/hooks/useMe'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { toast } from 'sonner'
import { ArrowLeft, Check, X } from 'lucide-react'

// 密碼複雜度驗證
const passwordSchema = z
  .string()
  .min(6, { message: '密碼長度至少需要 6 個字元' })
  .refine((val) => /[A-Z]/.test(val), { message: '密碼需包含至少一個大寫字母' })
  .refine((val) => /[a-z]/.test(val), { message: '密碼需包含至少一個小寫字母' })
  .refine((val) => /[0-9]/.test(val), { message: '密碼需包含至少一個數字' })
  .refine((val) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(val), {
    message: '密碼需包含至少一個特殊符號',
  })

const formSchema = z
  .object({
    old_password: z.string().min(1, { message: '請輸入目前密碼' }),
    new_password: passwordSchema,
    new_password_confirm: z.string().min(1, { message: '請確認新密碼' }),
  })
  .refine((data) => data.new_password === data.new_password_confirm, {
    message: '兩次輸入的密碼不一致',
    path: ['new_password_confirm'],
  })
  .refine((data) => data.old_password !== data.new_password, {
    message: '新密碼不能與目前密碼相同',
    path: ['new_password'],
  })

type FormValues = z.infer<typeof formSchema>

// 密碼強度檢查項目
const passwordChecks = [
  { label: '至少 6 個字元', test: (val: string) => val.length >= 6 },
  { label: '包含大寫字母', test: (val: string) => /[A-Z]/.test(val) },
  { label: '包含小寫字母', test: (val: string) => /[a-z]/.test(val) },
  { label: '包含數字', test: (val: string) => /[0-9]/.test(val) },
  {
    label: '包含特殊符號',
    test: (val: string) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(val),
  },
]

export default function ChangePassword() {
  const navigate = useNavigate()
  const { user, mutate } = useMe()
  const { token } = useAuth()
  const mustChangePassword = user?.mustChangePassword ?? false

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      old_password: '',
      new_password: '',
      new_password_confirm: '',
    },
  })

  const newPassword = form.watch('new_password')

  const onSubmit = async (data: FormValues) => {
    if (!token) {
      toast.error('未登入狀態')
      return
    }

    try {
      const response = await authService.changePassword(
        {
          old_password: data.old_password,
          new_password: data.new_password,
          new_password_confirm: data.new_password_confirm,
        },
        token,
      )

      if (response.success) {
        // 重新獲取用戶資料以更新 mustChangePassword 狀態
        await mutate()
        toast.success('密碼更改成功')
        navigate('/')
      } else {
        toast.error(response.error?.message || '密碼更改失敗')
      }
    } catch (error) {
      const message = axios.isAxiosError<{ error?: { message?: string } }>(error)
        ? error.response?.data?.error?.message
        : undefined
      toast.error(message || '發生錯誤，請稍後再試')
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden">
      {/* 背景裝飾 */}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-br from-blue-100/30 to-cyan-100/30 rounded-full filter blur-3xl transform translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-teal-100/30 to-blue-100/30 rounded-full filter blur-3xl transform -translate-x-1/3 translate-y-1/3" />

      {/* 置中內容 */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          {!mustChangePassword && (
            <Button
              variant="ghost"
              className="mb-4"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              返回
            </Button>
          )}

          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>更改密碼</CardTitle>
              <CardDescription>
                {mustChangePassword ? (
                  <span className="text-red-600">
                    您的密碼已過期，請立即更改密碼才能繼續使用系統。
                  </span>
                ) : (
                  '為了帳號安全，建議定期更改密碼。'
                )}
                新密碼不可與最近 5 次使用的密碼相同。
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="old_password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>目前密碼</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            placeholder="請輸入目前密碼"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="new_password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>新密碼</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            placeholder="請輸入新密碼"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* 密碼強度指示器 */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium mb-3">密碼強度檢查：</p>
                    <div className="grid grid-cols-2 gap-2">
                      {passwordChecks.map((check, index) => {
                        const passed = check.test(newPassword || '')
                        return (
                          <div
                            key={index}
                            className={`flex items-center text-sm ${
                              passed ? 'text-green-600' : 'text-gray-400'
                            }`}
                          >
                            {passed ? (
                              <Check className="h-4 w-4 mr-2" />
                            ) : (
                              <X className="h-4 w-4 mr-2" />
                            )}
                            {check.label}
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  <FormField
                    control={form.control}
                    name="new_password_confirm"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>確認新密碼</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            placeholder="請再次輸入新密碼"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex gap-4">
                    {!mustChangePassword && (
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1"
                        onClick={() => navigate(-1)}
                      >
                        取消
                      </Button>
                    )}
                    <Button
                      type="submit"
                      className={`bg-gradient-to-r from-[#1698B6] to-[#0F7A8D] ${mustChangePassword ? 'w-full' : 'flex-1'}`}
                    >
                      確認更改
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
